import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Button from 'Components/Atoms/Button/Button';
import getColor from 'color';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CATEGORY_COLORS as COLORS, ROUTES } from 'Utils/constants';
import useTaxonomies from 'Hooks/useTaxonomies';
import Input from 'Components/Atoms/Input/Input';
import Select from 'Components/Atoms/Select/Select';
import InputTags from 'Components/Molecules/InputTags/InputTags';
import ColorPicker from 'Components/Atoms/ColorPicker/ColorPicker';
import OutlineEdit from 'Components/Atoms/OutlineIcons/OutlineEdit';
import EmojiPicker from 'Components/Atoms/EmojiPicker/EmojiPicker';
import { useSnackbar } from 'Components/Molecules/SnackbarProvider/SnackbarProvider';
import Loading from 'Components/Atoms/Loading/Loading';

export default function TagEditorDrawer(props) {
    const { tag, onSubmit, onCancel, onClose, onError } = props;
    const navigate = useNavigate();

    const [processing, setProcessing] = useState(false);
    const [name, setName] = useState(tag.name);
    const [color, setColor] = useState(tag.color);
    const [emoji, setEmoji] = useState(tag.icon);
    const [keywords, setKeywords] = useState(tag.categorization_keywords || []);
    const [taxonomyId, setTaxonomyId] = useState('');

    const { taxonomies, isLoading } = useTaxonomies();
    const { enqueueSnackbar } = useSnackbar();
    const taxonomiesOptions = useMemo(() => {
        return taxonomies.map((t) => ({ value: t.id, label: t.name }));
    }, [taxonomies]);

    useEffect(() => {
        if (tag.taxonomy_id) {
            setTaxonomyId(tag.taxonomy_id);
        } else {
            setTaxonomyId(taxonomiesOptions[0]?.value || '');
        }
    }, [tag.taxonomy_id, taxonomiesOptions]);

    const { t } = useTranslation('categories');

    useEffect(() => {
        setName(tag.name);
        setColor(tag.color);
        setEmoji(tag.icon);
        setKeywords(tag.categorization_keywords || []);
    }, [tag]);

    const bgColor = useMemo(() => {
        return getColor(color).alpha(0.15).rgb().string();
    }, [color]);

    const handleSubmit = async (ev) => {
        ev.preventDefault();
        if (name.trim() === '') return;

        setProcessing(true);
        try {
            await onSubmit({
                name,
                color,
                icon: emoji,
                categorization_keywords: keywords,
                taxonomy_id: taxonomyId,
            });

            if (Array.isArray(keywords) && keywords.length > 0) {
                enqueueSnackbar(t('starting-transaction-tagging-process'), {
                    variant: 'initAsyncProcess',
                    key: `tagging-process-${Date.now()}`,
                });
            }
        } catch (error) {
            onError(error, {
                title: tag.id ? t('error-updating-tag-title') : t('error-creating-tag-title'),
                description: tag.id ? t('error-updating-tag-description') : t('error-creating-tag-description'),
            });
        } finally {
            setProcessing(false);
        }
    };

    if (isLoading) {
        return (
            <div className="text-center py-6">
                <Loading />
            </div>
        );
    }

    if (taxonomies.length === 0) {
        return (
            <div className="py-6">
                <div className="flex flex-col items-center justify-center gap-4 text-center py-8 px-4">
                    <p className="text-sm text-gray-500">{t('no-taxonomies-warning')}</p>
                    <Button
                        variant="primary"
                        onClick={() => {
                            onClose();
                            navigate(ROUTES.categories);
                        }}
                    >
                        {t('no-taxonomies-link')}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="h-full flex flex-col">
            <div className="flex justify-center mb-7 pt-2">
                <div style={{ backgroundColor: bgColor, color: color }} className="rounded-xl relative">
                    <OutlineEdit color="currentColor" className="absolute right-2 top-2 pointer-events-none" />

                    <EmojiPicker value={emoji} onChange={setEmoji} />
                </div>
            </div>

            <div className="grid gap-6 mb-6">
                <div className="flex gap-3">
                    <ColorPicker value={color} onChange={setColor} colors={COLORS} />

                    <Input
                        data-autofocus="true"
                        label={t('name')}
                        name="name"
                        value={name}
                        onChange={(ev) => setName(ev.target.value)}
                    />
                </div>

                <Select
                    label={t('taxonomy')}
                    value={taxonomyId}
                    onChange={setTaxonomyId}
                    options={taxonomiesOptions}
                    className="w-full"
                />

                <div className="bg-[#fff2ee] border border-[#ebe1e1] rounded-xl p-5 mt-2">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="text-orange-400">✨</span>
                        <span className="font-medium text-dark-blue">{t('automatic-assignment')}</span>
                    </div>
                    <p className="text-sm text-gray-500 mb-4">{t('automatic-assignment-description')}</p>
                    <InputTags tags={keywords} onChange={setKeywords} placeholder={t('keywords-placeholder')} />
                    <p className="text-sm text-gray-500 mt-3">{t('keywords-description')}</p>
                </div>
            </div>

            <div className="grow" />

            <div className="sticky bottom-0 bg-white pt-2 pb-4 z-10">
                <div className="flex gap-2">
                    <Button
                        variant="primary"
                        isBusy={processing}
                        type="submit"
                        className="grow"
                        testId="tag-editor.save"
                    >
                        {t('save')}
                    </Button>
                    <Button onClick={onCancel} className="grow" testId="tag-editor.cancel">
                        {t('cancel')}
                    </Button>
                </div>
            </div>
        </form>
    );
}

TagEditorDrawer.propTypes = {
    onCancel: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    tag: PropTypes.object.isRequired,
    onSubmit: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    onError: PropTypes.func.isRequired,
};
